import { Alert } from '../../components/ui/Alert.tsx';
import { EmptyState, Spinner } from '../../components/ui/Layout.tsx';
import type { FieldDefinition } from '../settings/use-fields.ts';
import { buildTimeline } from './timeline.ts';
import { useTimelineQuery } from './use-merchants.ts';

const DOT: Record<string, string> = {
  field: 'bg-control-border',
  merchant: 'bg-brand',
  tag: 'bg-success-solid',
  compliance: 'bg-danger-solid',
};

/** Timeline skeleton: field changes, tags, assignment, archive. Messages join it from M5. */
export function MerchantTimeline({
  workspaceId,
  merchantId,
  fields,
  people,
}: {
  workspaceId: string;
  merchantId: string;
  fields: FieldDefinition[];
  people: ReadonlyMap<string, string>;
}) {
  const sources = useTimelineQuery(workspaceId, merchantId);
  if (sources.isPending) return <Spinner label="Loading the timeline" />;
  if (sources.isError) return <Alert tone="error">{sources.error.message}</Alert>;
  const items = buildTimeline({ ...sources.data, fields, people });
  if (items.length === 0) return <EmptyState title="Nothing has happened yet" />;
  return (
    <ol className="flex flex-col gap-3" aria-label="Timeline">
      {items.map((item) => (
        <li key={item.key} className="flex gap-3" data-testid="timeline-item">
          <span
            className={`mt-1.5 size-2 shrink-0 rounded-pill ${DOT[item.kind] ?? DOT.field}`}
            aria-hidden
          />
          <div className="min-w-0">
            <p className="break-words callout text-ink">{item.title}</p>
            {item.detail ? (
              <p className="break-words footnote text-ink-muted">{item.detail}</p>
            ) : null}
            <p className="footnote text-ink-muted">
              {item.by} · <time dateTime={item.at}>{new Date(item.at).toLocaleString()}</time>
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}
